import type { TourStep } from '@/components/ui/Tour';
import {
  CLUB_PAGE_TOUR_STEP_IDS,
  INVENTORY_ITEM_CREATE_TOUR_STEP_IDS,
  TOUR_CENTERED_STEP_SELECTOR,
  TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
} from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface ClubInventoryTourOptions {
  onEnterItems?: () => void;
  onEnterHires?: () => void;
  onOpenCreateItem?: () => void;
  onCloseCreateItem?: () => void;
  onFinish?: () => void;
}

const openCreateItemDrawer = (options: ClubInventoryTourOptions) => () => {
  options.onEnterItems?.();
  options.onOpenCreateItem?.();
};

function inventoryItemCreateDrawerStep(
  onStepEnter?: () => void,
): Pick<TourStep, 'onStepEnter' | 'targetPollTimeoutMs' | 'waitForPanelReady'> {
  return {
    onStepEnter,
    targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
    waitForPanelReady: true,
  };
}

export function getInventoryItemCreateFormTourSteps(onStepEnter?: () => void): TourStep[] {
  const drawerStep = inventoryItemCreateDrawerStep(onStepEnter);

  return [
    {
      content: (
        <TourStepContent
          title="Add inventory"
          description="Work through each section — basics, stock and hire options, notes, and visibility — then save."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.DRAWER,
      position: 'left',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Item basics"
          description="Name the item, optionally categorize it, and set how many units your club owns."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.DETAILS,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Stock and hire"
          description="Track quantity on hand with low-stock alerts, mark items available for court hire add-ons or walk-up loans, and set a per-hour hire price."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.OPTIONS,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Notes"
          description="Add internal notes for staff — serial numbers, storage location, or maintenance reminders."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.NOTES,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Active"
          description="Keep the item active so it appears in hire and stock lists. Deactivate to hide it without deleting history."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.ACTIVE,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Save the item"
          description="Create the item to include it in hire availability and stock counts."
        />
      ),
      selectorId: INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.SUBMIT,
      position: 'top',
      ...drawerStep,
    },
  ];
}

export function getClubInventoryTourSteps(options: ClubInventoryTourOptions = {}): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Items tab"
          description="Track equipment and stock your club owns. Mark items as hireable for court hire add-ons or walk-up loans."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_TAB_ITEMS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateItem?.();
        options.onEnterItems?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Add an item"
          description="Create inventory here — we'll walk through the form next."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS_NEW,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateItem?.();
        options.onEnterItems?.();
      },
    },
    ...getInventoryItemCreateFormTourSteps(openCreateItemDrawer(options)).map((step) =>
      step.selectorId === INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.SUBMIT
        ? {
            ...step,
            content: (
              <TourStepContent
                title="Save the item"
                description="Create the item to include it in hire availability and stock counts. You can close without saving to continue the tour."
              />
            ),
          }
        : step,
    ),
    {
      content: (
        <TourStepContent
          title="Hires tab"
          description="Walk-up and court-booking equipment loans appear here. Mark items returned when they come back to the desk."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_TAB_HIRES,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateItem?.();
        options.onEnterHires?.();
      },
    },
  ];
}

/** @deprecated Use getClubInventoryTourSteps for the unified page tour. */
export const clubInventoryItemsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Inventory items"
        description="Track equipment and stock your club owns. Mark items as hireable for court hire add-ons or walk-up loans."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Item list"
        description="Each item shows quantity on hand and whether it is hireable or stock-tracked. Adjust stock when deliveries arrive."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="Add an item"
        description="Create inventory when you're ready — we'll tour the form on your first add."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_ITEMS_NEW,
    position: 'bottom',
  },
];

/** @deprecated Use getClubInventoryTourSteps for the unified page tour. */
export const clubInventoryHiresTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Active hires"
        description="Walk-up and court-booking equipment loans appear here. Mark items returned when they come back to the desk."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Hire log"
        description="Review who has what, for how long, and whether the loan is still out or completed."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.INVENTORY_HIRES,
    position: 'top',
  },
];

export const inventoryItemCreateTourSteps: TourStep[] = getInventoryItemCreateFormTourSteps();
